import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, map, switchMap, tap } from 'rxjs';
import { Meeting } from '../models/meeting';
import { SchoolUser } from './role.service';
import { IGenericJSON } from '../helpers/interface';
import { WebsocketService } from './websocket.service';

/**
 * A faculty member booking a meeting with one of their own students. Mirrors
 * MeetingRequestPayload but in the opposite direction: there the caller is the
 * student and the faculty member is chosen, here the caller is the faculty
 * member and the student is chosen.
 *
 * `status` is deliberately absent — these are auto-approved server-side because
 * a faculty member created them. A client that can post its own status is a
 * client that can confirm a student's request.
 */
export interface CreateMeetingPayload {
  /** The student's *user* id, matching how facultyUserId is used below. */
  studentUserId: number;
  scheduledStart: Date;
  durationMinutes: number;
  title: string;
  description: string;
  meetingType: 'remote' | 'in-person';
  /**
   * For a remote meeting, the link the counselor supplies themselves — these
   * are confirmed on creation, so there's no approval step to generate one at,
   * and no calendar integration to generate it from yet. Null for in-person.
   */
  meetingLink: string | null;
}

export interface MeetingRequestPayload {
  /** The faculty member's *user* id — meetings are booked by user, since any
   * faculty user can be booked whether or not they have a counselor profile. */
  facultyUserId: number;
  facultyName: string;
  scheduledStart: Date;
  durationMinutes: number;
  title: string;
  reason: string;
  meetingType: 'remote' | 'in-person';
}

@Injectable({
  providedIn: 'root',
})
export class MeetingService {
  private http = inject(HttpClient);
  private wsService = inject(WebsocketService);
  private base = '/api/meetings';

  private meetingsChanged = new Subject<void>();
  /** Emits whenever a meeting is created, or its status/schedule changes — lets
   * views that derive counts (e.g. nav badges) refresh without re-polling.
   *
   * Only this client's own mutations emit. A meeting notification arriving over
   * the socket refreshes nothing, so a notification deep link clicked while
   * already on a meetings page can focus a meeting as it was before the event —
   * or miss one the event created. Live updates are the fix; when they land, the
   * refresh has to run through each page's load path, since that is what calls
   * FocusTarget#check(). Patching the array in place would leave the deep link
   * broken with the list looking correct. */
  meetingsChanged$ = this.meetingsChanged.asObservable();

  constructor() {
    // Mirrors NotificationService.start()'s reconnect safe pattern:
    // onReconnect() fires on the initial connect and every reconnect after.
    // tap() refetches immediately to catch up on anything missed while
    // disconnected (the server doesn't replay drops), and switchMap()
    // reattaches the live listener to the current socket each time
    this.wsService
      .onReconnect()
      .pipe(
        tap(() => this.meetingsChanged.next()),
        switchMap(() => this.wsService.on<{ meeting_id: number }>('meeting:updated')),
      )
      .subscribe(() => this.meetingsChanged.next());
  }

  /** Fetches every meeting the logged-in user is a participant of. */
  getMeetings(): Observable<Meeting[]> {
    return this.http
      .get<IGenericJSON[]>(this.base)
      .pipe(map((data: any) => Meeting.digestResponse(data)));
  }

  getAvailableFaculty(): Observable<SchoolUser[]> {
    return this.http.get<SchoolUser[]>(`${this.base}/available-faculty`);
  }
  /** Requests a meeting as a student — lands 'pending' until the counselor confirms. */
  requestMeeting(payload: MeetingRequestPayload): Observable<Meeting> {
    const start = payload.scheduledStart;
    const isRemote = payload.meetingType === 'remote';
    const body = {
      faculty_user_id: payload.facultyUserId,
      title: payload.title,
      description: payload.reason,
      meetingDate: MeetingService.toDateString(start),
      startTime: MeetingService.toTimeString(start),
      duration: payload.durationMinutes,
      // Remote meetings show as "remote" with a placeholder link until the
      // counselor confirms and generates the real one; in-person carry a room.
      location: isRemote ? '' : 'TBD',
      meetingLink: isRemote ? 'Link pending' : null,
      status: 'pending',
    };

    return this.http.post<IGenericJSON>(this.base, body).pipe(
      map((data) => Meeting.digest(data)),
      tap(() => this.meetingsChanged.next()),
    );
  }

  /**
   * Books a meeting the faculty member created themselves, which the server
   * confirms on creation rather than leaving pending.
   */
  createMeeting(payload: CreateMeetingPayload): Observable<Meeting> {
    const start = payload.scheduledStart;
    const isRemote = payload.meetingType === 'remote';
    const body = {
      student_user_id: payload.studentUserId,
      title: payload.title,
      description: payload.description,
      meetingDate: MeetingService.toDateString(start),
      startTime: MeetingService.toTimeString(start),
      duration: payload.durationMinutes,
      location: isRemote ? '' : 'TBD',
      // The counselor's own link
      meetingLink: isRemote ? payload.meetingLink : null,
    };

    return this.http.post<IGenericJSON>(`${this.base}/faculty`, body).pipe(
      map((data) => Meeting.digest(data)),
      tap(() => this.meetingsChanged.next()),
    );
  }

  /** Cancels a meeting on behalf of a participant. */
  cancelMeeting(id: number): Observable<void> {
    return this.http
      .put<void>(`${this.base}/${id}/cancel`, {})
      .pipe(tap(() => this.meetingsChanged.next()));
  }

  /** Confirms a pending request or an in-flight reschedule proposal. */
  confirmMeeting(id: number): Observable<Meeting> {
    return this.http
      .put<{ message: string; meeting: IGenericJSON }>(`${this.base}/${id}/confirm`, {})
      .pipe(
        map((data) => Meeting.digest(data.meeting)),
        tap(() => this.meetingsChanged.next()),
      );
  }

  /** Denies a pending request, or rejects a reschedule proposal (reverting to the original time). */
  denyMeeting(id: number, note?: string): Observable<void> {
    return this.http
      .put<void>(`${this.base}/${id}/deny`, note ? { note } : {})
      .pipe(tap(() => this.meetingsChanged.next()));
  }

  /** Sets or replaces a counselor's freeform note on a meeting. */
  updateMeetingNote(id: number, note: string): Observable<Meeting> {
    return this.http
      .put<{ message: string; meeting: IGenericJSON }>(`${this.base}/${id}/note`, { note })
      .pipe(
        map((data) => Meeting.digest(data.meeting)),
        tap(() => this.meetingsChanged.next()),
      );
  }

  /**
   * Proposes a change to an existing meeting. Includes any combination of a new
   * date/time/duration, location/meetingLink (also how switching between
   * remote and in-person is proposed), or an optional message explaining the
   * proposal. Applied immediately if the meeting is still pending and the caller is the
   * one who requested it, otherwise held until the other participant accepts
   * or declines it via `confirmMeeting`/`denyMeeting`.
   */
  proposeChanges(
    id: number,
    changes: {
      meetingDate?: string;
      startTime?: string;
      duration?: number;
      location?: string;
      meetingLink?: string | null;
      message?: string;
    },
  ): Observable<void> {
    return this.http
      .put<void>(`${this.base}/${id}/request-update`, changes)
      .pipe(tap(() => this.meetingsChanged.next()));
  }

  /**
   * Fetches how many pending/confirmed meetings a faculty member has on each
   * upcoming date, across all students — used to grey out days where they've
   * already hit their daily meeting cap.
   * @returns Observable of a map from 'YYYY-MM-DD' to meeting count.
   */
  getMeetingCountsByDate(facultyUserId: number): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(`${this.base}/faculty/${facultyUserId}/counts`);
  }

  /** Formats a Date as a local "YYYY-MM-DD" string (no UTC conversion). */
  private static toDateString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /** Formats a Date as a local "HH:MM:SS" string (no UTC conversion). */
  private static toTimeString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
}
